/**
 * Per-tenant theme tokens.
 *
 * A tenant ships a `theme.json`-shaped object; the console turns it into CSS custom properties
 * on <html> at runtime. Tailwind's @theme maps its colour utilities onto these same properties,
 * so one JS assignment re-skins the whole app without a rebuild.
 *
 * The classic unreadable-UI bug is "a token defined in light but not in dark". That is made
 * impossible here twice over: `TokenMap` is `Record<TokenKey, string>`, so an incomplete palette
 * is a TYPE ERROR; and `test/theme.test.ts` re-checks it at runtime for themes that arrive as
 * untyped JSON from the API, plus asserts WCAG AA contrast on the pairs that carry text.
 *
 * Author: Danish Khan
 */

export const TOKEN_KEYS = [
  // Ground
  'bg',
  'bg-subtle',
  'surface',
  'surface-2',
  'surface-3',
  // Ink
  'fg',
  'fg-muted',
  'fg-subtle',
  // Lines
  'border',
  'border-strong',
  // Brand — see the block comment above `light` for which of these two carries text.
  'brand',
  'brand-hover',
  'brand-fg',
  'brand-ink',
  'brand-soft',
  'brand-soft-fg',
  'focus',
  // Status
  'ok',
  'ok-soft',
  'ok-soft-fg',
  'warn',
  'warn-soft',
  'warn-soft-fg',
  'danger',
  'danger-soft',
  'danger-soft-fg',
  'info',
  'info-soft',
  'info-soft-fg',
  'neutral-soft',
  'neutral-soft-fg',
  // Lead tiers (pack scoring.yaml: hot >= 70, warm >= 40, cold >= 0)
  'tier-hot',
  'tier-hot-fg',
  'tier-warm',
  'tier-warm-fg',
  'tier-cold',
  'tier-cold-fg',
  // WhatsApp thread
  'bubble-in',
  'bubble-in-fg',
  'bubble-out',
  'bubble-out-fg',
  'bubble-human',
  'bubble-human-fg',
  // Chrome
  'overlay',
  'shadow',
] as const;

export type TokenKey = (typeof TOKEN_KEYS)[number];
export type TokenMap = Record<TokenKey, string>;
export type ThemeMode = 'light' | 'dark';

export interface ThemeJson {
  /** Tenant id this theme belongs to — `default` is the HeyOne house palette. */
  id: string;
  name: string;
  /** Font stacks. Self-hosted only; see src/assets/fonts.css. */
  fontSans: string;
  fontMono: string;
  /** Base corner radius in px. */
  radius: number;
  light: TokenMap;
  dark: TokenMap;
}

/**
 * The brand orange, split by role.
 *
 * `#FF4F00` measures 3.30:1 on white. That clears WCAG AA for non-text UI (1.4.11, 3:1) and for
 * large text (1.4.3, 3:1 at >= 24px or >= 18.66px bold), and it fails AA for body text (4.5:1).
 * The colour is therefore not wrong — it is wrong for one job. So there are two tokens and the
 * job decides which one you reach for:
 *
 *   --h1-brand      fills, icons, borders, rings, chart marks, large display text
 *   --h1-brand-ink  body text, small labels, and (light only) a fill behind WHITE text
 *
 * Light `brand-ink` is `#C63E00`, and the value is chosen against BOTH light grounds rather than
 * against white alone: 5.13:1 on `surface`/`bubble-in` (#FFFFFF) and 4.79:1 on the page ground
 * `bg` (#F6F7F9), plus 5.13:1 the other way round with white on top, so it doubles as the one
 * brand fill a white label is allowed on. Hue 18.8 deg against the brand's 18.6 deg, so it still
 * reads as the same orange rather than as a second colour. Deriving a text token against #FFFFFF
 * only is the trap here — `#D14200` clears 4.68:1 on white and just 4.36:1 on `bg`, which would
 * have left "Explain this turn" in the Inbox and Login's mock-build hint failing AA on the exact
 * ground they are painted on.
 *
 * On dark ground the split collapses: `#FF4F00` is 5.25:1 on `surface` and 5.74:1 on `bg`, so the
 * brand orange IS the accessible text colour there and darkening it would make it worse. That is
 * why `brand-ink` is the brand orange in dark mode and only differs in light mode.
 *
 * The grounds brand-ink is painted on are asserted in test/theme.test.ts. Two it does NOT reach,
 * and nothing paints brand text on either: light `surface-3` (#E6E9EF, 4.22:1) and dark
 * `surface-3` (#272E39, 4.15:1). Put brand body text on a `surface-3` panel and it is a new
 * decision, not a free one.
 *
 * Browser chrome — the `theme-color` meta, the manifest's `theme_color`, the favicon — stays
 * `#FF4F00` and is not covered by either token: it is a brand surface, not text on one.
 */
const light: TokenMap = {
  bg: '#F6F7F9',
  'bg-subtle': '#EDEFF3',
  surface: '#FFFFFF',
  'surface-2': '#F2F4F7',
  'surface-3': '#E6E9EF',
  fg: '#12161C',
  'fg-muted': '#545C69',
  'fg-subtle': '#6E7684',
  border: '#DDE1E8',
  // 3.3:1 on surface, 3.1:1 on bg. This is the border a form control gets, so it is a
  // WCAG 1.4.11 target (3:1), not decoration — `border` is the decorative row separator.
  'border-strong': '#858E9C',
  brand: '#FF4F00',
  'brand-hover': '#E64700',
  // Near-black on orange: white on #FF4F00 is 3.30:1, which fails AA for body text. Anything
  // sitting ON a brand fill in light mode takes this, not white.
  'brand-fg': '#240B00',
  // 5.13:1 on `surface` #FFFFFF, 4.79:1 on `bg` #F6F7F9, and 5.13:1 with white on top of it — so
  // it clears AA on BOTH grounds the console paints brand text on, and doubles as the one brand
  // fill a white label is allowed on. Do not lighten it towards the brand without re-measuring
  // against `bg`, which is the tighter of the two.
  'brand-ink': '#C63E00',
  'brand-soft': '#FFEDE4',
  'brand-soft-fg': '#8F2B00',
  focus: '#0B63CE',
  ok: '#0E7C46',
  'ok-soft': '#E1F5E9',
  'ok-soft-fg': '#0A5A33',
  warn: '#9A6200',
  'warn-soft': '#FDF1DC',
  'warn-soft-fg': '#6F4700',
  danger: '#C02616',
  'danger-soft': '#FDE8E5',
  'danger-soft-fg': '#8E1C10',
  info: '#0B63CE',
  'info-soft': '#E4EEFB',
  'info-soft-fg': '#0A4C9E',
  'neutral-soft': '#EAEDF2',
  'neutral-soft-fg': '#414957',
  'tier-hot': '#C02616',
  'tier-hot-fg': '#8E1C10',
  'tier-warm': '#9A6200',
  'tier-warm-fg': '#6F4700',
  'tier-cold': '#5A6373',
  'tier-cold-fg': '#414957',
  'bubble-in': '#FFFFFF',
  'bubble-in-fg': '#12161C',
  'bubble-out': '#DCF3E4',
  'bubble-out-fg': '#0F2419',
  'bubble-human': '#E4EEFB',
  'bubble-human-fg': '#0D2136',
  overlay: 'rgba(14, 18, 24, 0.44)',
  shadow: 'rgba(14, 18, 24, 0.10)',
};

const dark: TokenMap = {
  bg: '#0E1116',
  'bg-subtle': '#0A0D11',
  surface: '#161B22',
  'surface-2': '#1E242D',
  'surface-3': '#272E39',
  fg: '#E8ECF2',
  'fg-muted': '#A3AEBE',
  'fg-subtle': '#8A94A4',
  border: '#2B323C',
  // 3.4:1 on surface, 3.7:1 on bg — see the note on the light palette.
  'border-strong': '#646E80',
  // The brand orange itself, not a lightened stand-in: on this ground it measures 5.25:1 on
  // `surface` and 5.74:1 on `bg`, so it carries text unmodified and stays on-brand doing it.
  brand: '#FF4F00',
  'brand-hover': '#FF8347',
  'brand-fg': '#1A0700',
  // Deliberately identical to `brand`. Dark mode needs no second orange; the split exists only
  // because white ground is what #FF4F00 cannot carry text on.
  'brand-ink': '#FF4F00',
  'brand-soft': '#3A1B0C',
  'brand-soft-fg': '#FFB08A',
  focus: '#6AA9F5',
  ok: '#35C07B',
  'ok-soft': '#0F2B1D',
  'ok-soft-fg': '#79DCA8',
  warn: '#E0A030',
  'warn-soft': '#33240A',
  'warn-soft-fg': '#F0C579',
  danger: '#F2685A',
  'danger-soft': '#3A1614',
  'danger-soft-fg': '#F9A79C',
  info: '#5BA3F5',
  'info-soft': '#10233A',
  'info-soft-fg': '#9CC8FA',
  'neutral-soft': '#222933',
  'neutral-soft-fg': '#B6C0CE',
  'tier-hot': '#F2685A',
  'tier-hot-fg': '#F9A79C',
  'tier-warm': '#E0A030',
  'tier-warm-fg': '#F0C579',
  'tier-cold': '#8A94A4',
  'tier-cold-fg': '#B6C0CE',
  'bubble-in': '#1E242D',
  'bubble-in-fg': '#E8ECF2',
  'bubble-out': '#103528',
  'bubble-out-fg': '#C6EBD7',
  'bubble-human': '#122A44',
  'bubble-human-fg': '#CBE1F8',
  overlay: 'rgba(3, 5, 8, 0.66)',
  shadow: 'rgba(0, 0, 0, 0.5)',
};

export const DEFAULT_THEME: ThemeJson = {
  id: 'default',
  name: 'HeyOne',
  fontSans: "'Hanken Grotesk', 'Noto Sans Devanagari', system-ui, -apple-system, 'Segoe UI', sans-serif",
  fontMono: "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, monospace",
  radius: 10,
  light,
  dark,
};

/** Keys present in one mode but not the other. Empty array = a complete theme. */
export function missingTokens(theme: ThemeJson): string[] {
  const gaps: string[] = [];
  for (const key of TOKEN_KEYS) {
    if (!theme.light[key]) gaps.push(`light.${key}`);
    if (!theme.dark[key]) gaps.push(`dark.${key}`);
  }
  return gaps;
}

/**
 * Write a theme onto an element as CSS custom properties.
 * Any token the tenant omitted falls back to the HeyOne default rather than to nothing —
 * an unset custom property renders as `unset`, which is how a panel ends up black-on-black.
 */
export function applyTheme(theme: ThemeJson, mode: ThemeMode, el: HTMLElement): void {
  const map = mode === 'dark' ? theme.dark : theme.light;
  const fallback = mode === 'dark' ? DEFAULT_THEME.dark : DEFAULT_THEME.light;
  for (const key of TOKEN_KEYS) el.style.setProperty(`--h1-${key}`, map[key] || fallback[key]);
  el.style.setProperty('--h1-font-sans', theme.fontSans || DEFAULT_THEME.fontSans);
  el.style.setProperty('--h1-font-mono', theme.fontMono || DEFAULT_THEME.fontMono);
  el.style.setProperty('--h1-radius', `${theme.radius || DEFAULT_THEME.radius}px`);
  el.dataset['theme'] = mode;
  el.style.colorScheme = mode;
}

export const THEME_STORAGE_KEY = 'h1.theme-mode';

export function preferredMode(): ThemeMode {
  try {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch {
    /* private window, blocked storage — fall through to the OS preference */
  }
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}
